import React, { useEffect, useRef, useState } from 'react';
import NavigationBar from '../../components/NavigationBar/NavigationBar';
import '../../App.css';
import { Link, useNavigate } from 'react-router-dom';
import Footer from '../../components/Common/Footer';
import Dialogs from '../../components/Common/Dialog';
import { archiveBook, handleDeleteBook, unArchiveBook } from '../Builder/services/builderHelpers';
import Guide from '../../components/Common/Guide';
import PlanPurchase from '../../components/Common/PlanPurchase';
import ThankYou from '../../components/Common/ThankYou';
import { Toast } from '../../components/Common/Toast';
import Heading from '../../components/Common/Heading';
import { PageDetailsModel } from '../../interfaces/common.interface';
import { BookModel } from '../../interfaces/book.interface';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { runEditStory } from '../../store/storySlice';
import {
  fetchBooks,
  generatePdf,
  printBooks,
  recreateImage,
  shipBooks,
  showArchivedBooks,
  updateBookText,
} from './services/Debug.service';
import ModalComponent from './components/ModalComponent';
import BookMenu from './components/BookMenu';
import BookCover from './components/BookCoverDetails';
import Checkbox from '../../components/Common/Checkbox';
import PrimaryFixedBulkActionManu from '../../components/Common/PrimaryFixedBulkActionManu';
import Modal from '../../components/Common/modal/Modal';
import ShippingAddressInfo from '../../components/ShippingAddressInfo/ShippingAddressInfo';

interface PageModalProps {
  setBookUpdateLoader?: (value: boolean) => void;
}

function BookList() {
  const user = useSelector((state: RootState) => state.user.user);
  const navigateTo = useNavigate();
  const [books, setBooks] = useState<BookModel[]>([]);
  const [archivedBooks, setArchivedBooks] = useState<'yes' | 'no'>('no');
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingPDF, setLoadingPDF] = useState<boolean[]>([]);
  const [deletingBook, setDeletingBook] = useState<boolean>(false);
  const [createImageLoader, setCreateImageLoader] = useState<boolean>(false);
  const [bookUpdateLoader, setBookUpdateLoader] = useState<boolean>(false);
  const [userCreditBalance, setUserCreditBalance] = useState<number>(0);
  const [stripeSubscriptionStatus, setStripeSubscriptionStatus] = useState<boolean>(true);
  const [purchasePlan, setPurchasePlan] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [selectedBook, setSelectedBook] = useState<{ bookId: string | null; index: number | null }>({
    bookId: null,
    index: null,
  });
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number | null>(null);
  const [showThanksNote, setShowThanksNote] = useState<boolean>(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPage, setSelectedPage] = useState<PageDetailsModel | null>(null);

  const [useSelectMode, setUseSelectMode] = useState<boolean>(false);
  const [selectedBooks, setSelectedBooks] = useState<Map<string, BookModel>>(new Map());
  const [selectedPurchasedBooks, setSelectedPurchasedBooks] = useState<number>(0);
  const [allPurchasedBooks, setAllPurchasedBooks] = useState<number>(0);

  const [printingLoader, setPrintingLoader] = useState<boolean>(false);
  const [shippingLoader, setShippingLoader] = useState<boolean>(false);
  const [shippingPopup, setShippingPopup] = useState<boolean>(false);

  const sumPurchasedBooks = (books: Map<string, BookModel>) => {
    setSelectedPurchasedBooks(
      [...books?.values()].reduce((acc, item) => {
        acc += item.purchasedCounter || 0;
        return acc;
      }, 0),
    );
  };

  const toggleSelect = (book: BookModel) => {
    setSelectedBooks((prev) => {
      const newMap = new Map(prev);

      if (newMap.has(book.bookId!)) {
        newMap.delete(book.bookId!);
      } else {
        newMap.set(book.bookId!, book);
      }

      sumPurchasedBooks(newMap);
      return newMap;
    });
  };

  const selectAll = () => {
    if (selectedBooks.size === books.length) {
      const newMap = new Map<string, BookModel>();
      setSelectedBooks(newMap);
      sumPurchasedBooks(newMap);
    } else {
      const newMap = new Map<string, BookModel>();
      books.forEach((book) => {
        if (book.bookId) {
          newMap.set(book.bookId, book);
        }
      });
      setSelectedBooks(newMap);
      sumPurchasedBooks(newMap);
    }
  };

  const isSelected = (bookId: string) => selectedBooks.has(bookId);

  const closeSelectMode = () => {
    setUseSelectMode(false);
    setSelectedBooks(new Map());
  };

  const handlePrintSelectedBooks = () => {
    const selectedBookIds: string[] = [...selectedBooks?.values()]
      .map((book) => book?.bookId)
      .filter((bookId): bookId is string => !!bookId);
    printBooks(selectedBookIds, setPrintingLoader).then().catch();
  };

  const handleShipSelectedBooks = async () => {
    const selectedBookIds: string[] = [...selectedBooks?.values()]
      .map((book) => book?.bookId)
      .filter((bookId): bookId is string => !!bookId);
    await shipBooks(selectedBookIds, setShippingLoader, () => {
      handleFetchBooks().then().catch();
    });
  };

  const dispatch: AppDispatch = useDispatch();

  useEffect(() => {
    if (user) {
      if (user?.stripeSubscriptionId || user.isTrialFreeAccess) {
        handleFetchBooks().then();
      } else {
        setStripeSubscriptionStatus(false);
      }
    }
  }, [currentPage]);

  useEffect(() => {
    if (user) {
      if (user.bookFlyersGenerationProgress === 100 && user.bookFlyersPdfPath) {
        // downloadPDFfile(user.bookFlyersPdfPath).then().catch();
      }
      if (user.isTeacherMode) {
        navigateTo('/classroom/library');
      }
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      return;
    }
    setUserCreditBalance(user?.creditBalance || 0);
    if (archivedBooks) {
      handleShowArchivedBooks(user?.email!).then();
    } else {
      handleFetchBooks().then();
    }
  }, [user?.creditBalance, archivedBooks]);

  useEffect(() => {
    setAllPurchasedBooks(
      books?.reduce((acc, item) => {
        acc += item?.purchasedCounter || 0;
        return acc;
      }, 0),
    );
  }, [books]);

  const handleFetchBooks = async (): Promise<void> => {
    await fetchBooks(user?.email, archivedBooks, setLoading, setBooks, setTotalPages);
  };

  const handleShowArchivedBooks = async (email: string): Promise<void> => {
    await showArchivedBooks(email, archivedBooks, setLoading, setBooks, setTotalPages);
  };

  const toggleLoaderPDF = (index: number, showLoader = true): void => {
    const newLoadingState: any = [...loadingPDF];
    if (showLoader) newLoadingState[index] = !newLoadingState[index];
    else newLoadingState[index] = false;
    setLoadingPDF(newLoadingState);
  };

  const handleUpdateBookText = async (
    bookId: string,
    pageIndex: number,
    updatedText: string,
    updatedScene: string,
  ): Promise<void> => {
    await updateBookText(bookId, pageIndex, updatedText, updatedScene);
  };

  const handleClose = (): void => {
    setOpenDialog(false);
  };

  const handleGeneratePdf = async (bookId: string, index: number): Promise<void> => {
    await generatePdf(bookId, toggleLoaderPDF, index, books, setBooks);
  };

  const handleClick = (): void => {
    const type = archivedBooks === 'yes' ? 'no' : 'yes';
    setArchivedBooks(type);
  };

  const closeModal = (): void => {
    setModalOpen(false);
    setSelectedPage(null);
  };

  const handleRecreateImage = async (bookId: string, pageIndex?: number): Promise<void> => {
    if (userCreditBalance <= 0) {
      Toast('Additional Image Credits Needed', true);
      return;
    }

    setCreateImageLoader(true);

    try {
      await recreateImage(bookId, pageIndex);
    } catch (error: any) {
      Toast(error?.message! || 'Image Not Generated', true);
    } finally {
      setCreateImageLoader(false);
    }
  };

  const PageModal: React.FC<PageModalProps> = ({ setBookUpdateLoader }) => {
    const textRef = useRef<HTMLTextAreaElement | null>(null);
    const sceneRef = useRef<HTMLTextAreaElement | null>(null);

    const handleUpdateBook = async () => {
      setBookUpdateLoader && setBookUpdateLoader(true);
      const updatedText = textRef.current ? textRef.current.value : '';
      const updatedScene = sceneRef.current ? sceneRef.current.value : '';

      if (selectedPage && selectedPage.bookId && selectedPage.pageIndex) {
        try {
          await handleUpdateBookText(selectedPage.bookId, selectedPage.pageIndex, updatedText, updatedScene);
          closeModal(); // Close the modal after successful update
          setBookUpdateLoader && setBookUpdateLoader(false);
        } catch (error) {
          setBookUpdateLoader && setBookUpdateLoader(false);
        }
      } else {
        setBookUpdateLoader && setBookUpdateLoader(false);
      }
    };

    if (!selectedPage) return null;

    return (
      <ModalComponent
        modalOpen={modalOpen}
        selectedPage={selectedPage}
        bookUpdateLoader={bookUpdateLoader}
        createImageLoader={createImageLoader}
        handleUpdateBook={handleUpdateBook}
        handleRecreateImage={handleRecreateImage}
        closeModal={closeModal}
      />
    );
  };

  const handleBookNavigation = (book: BookModel, navigateToEdit: boolean = false): void => {
    if (navigateToEdit) {
      dispatch(
        runEditStory({
          book,
          navigate: navigateTo,
        }),
      );
      return;
    }
    if (book?.coverURL || !stripeSubscriptionStatus) {
      navigateTo(`/bookflip/${book.id}`);
    } else {
      dispatch(
        runEditStory({
          book,
          navigate: navigateTo,
        }),
      );
    }
  };

  const handleEditBook = (book: BookModel): void => {
    stripeSubscriptionStatus
      ? dispatch(
          runEditStory({
            book,
            navigate: navigateTo,
          }),
        )
      : Toast('You need an active membership to edit your story', true);
  };

  const goBack = (): void => {
    navigateTo(-1);
    setShowThanksNote(false);
  };

  return (
    <div className="App mx-auto">
      <NavigationBar />
      {showThanksNote ? (
        <ThankYou goBack={goBack} />
      ) : (
        <>
          <div className="w-full bg-storytan min-h-[90vh]">
            {purchasePlan ? (
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <PlanPurchase setShowThanksNote={setShowThanksNote} className={'justify-start'} />
              </div>
            ) : (
              <div className="max-w-7xl mx-auto p-4">
                <div className="flex justify-between items-center">
                  {!useSelectMode && (
                    <div className="flex flex-col md:flex-row items-center align-end mb-4">
                      <div className="flex">
                        <Heading title="My Library" />
                        <div>
                          <Guide />
                        </div>
                        {user?.librarySelectMode && (
                          <button
                            className="flex justify-center border-storyblueSecondLightText border text-storyblueSecondLightText hover:text-storyblueSecondLightText font-bold rounded-full text-sm px-4 py-2 h-9 ml-4"
                            onClick={() => setUseSelectMode(!useSelectMode)}
                            type="button"
                          >
                            Select
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                  {useSelectMode && (
                    <div className=" flex items-center align-end mb-4">
                      <Checkbox checked={selectedBooks.size === books.length} onCheckedChange={selectAll} />
                      <span className="text-lg font-semibold ml-2">Select all ({books.length})</span>
                    </div>
                  )}

                  {user?.librarySelectMode && (
                    <div className="flex">
                      <span className="text-storyblue mr-4 md:mr-14">
                        Students with Purchased Books – {useSelectMode ? selectedPurchasedBooks : allPurchasedBooks}
                      </span>
                    </div>
                  )}

                  <button
                    onClick={handleClick}
                    className="flex justify-center items-center pointer-grab text-white text-sm rounded-2xl px-4 py-2 bg-storyblue shadow-xl font-bold capitalize"
                  >
                    {archivedBooks === 'yes' ? 'Hide' : 'Show'} Archived Stories
                  </button>
                </div>
                <div className="book-list grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                  {loading ? (
                    <div className="col-span-3 mx-auto ">
                      <h1 className="text-storyblue text-xl font-bold">
                        <img className="w-12 h-12" src="/loading.gif" alt="Loading" />
                      </h1>
                    </div>
                  ) : books?.length > 0 ? (
                    (user?.isTrialFreeAccess
                      ? [
                          ...books,
                          {
                            isDemoBlocked: true,
                            coverURL: books[books.length - 1].coverURL,
                          },
                        ]
                      : books
                    )?.map((book, index) => (
                      <div
                        key={book?.id}
                        className="p-0 md:p-1 book-item font-sans text-white bg-storytan uppercase text-2xl font-bold rounded-md mx-auto mb-11 relative"
                      >
                        {useSelectMode && (
                          <Checkbox
                            checked={isSelected(book.bookId!)}
                            onCheckedChange={() => toggleSelect(book)}
                            className={`absolute left-6 top-7 border-none z-10 w-7 h-7`}
                          />
                        )}

                        <Link
                          to="#"
                          onClick={() =>
                            useSelectMode
                              ? toggleSelect(book)
                              : !book?.isDemoBlocked && handleBookNavigation(book, false)
                          }
                        >
                          <BookCover book={book} user={user} isSelected={isSelected(book.bookId!)} />
                        </Link>

                        {user?.librarySelectMode && (
                          <div className="flex justify-between text-black bg-storytan capitalize text-sm font-light px-4">
                            <span>
                              Viewed - <span>{book?.viewCounter || 0}</span>
                            </span>
                            {book?.purchasedCounter === 0 && (book?.shippedCounter || 0) > 0 ? (
                              <span>Sent for printing</span>
                            ) : (
                              <span>
                                Purchased - <span>{book?.purchasedCounter || 0}</span>
                              </span>
                            )}
                          </div>
                        )}

                        {!!book?.isDemoBlocked ? (
                          ''
                        ) : (
                          <BookMenu
                            book={book}
                            user={user}
                            className={`absolute top-[1.5rem] right-[1.7rem]`}
                            mode="default"
                            index={index}
                            archivedBooks={archivedBooks}
                            stripeSubscriptionStatus={stripeSubscriptionStatus}
                            loadingPDF={loadingPDF}
                            selectedBook={selectedBook}
                            handleEditBook={handleEditBook}
                            handleGeneratePdf={handleGeneratePdf}
                            archiveBook={archiveBook}
                            unArchiveBook={unArchiveBook}
                            handleFetchBooks={handleFetchBooks}
                            setSelectedBook={setSelectedBook}
                            setOpenDialog={setOpenDialog}
                          />
                        )}
                      </div>
                    ))
                  ) : stripeSubscriptionStatus && books?.length === 0 ? (
                    <div className="col-span-3 h-[60vh] flex items-center mx-auto">
                      <h1 className="text-storyblue  text-xl font-bold">No Book Found</h1>
                    </div>
                  ) : (
                    <div className="col-span-3 h-[60vh] mt-[4rem] flex flex-col items-center mx-auto">
                      <h1 className="text-storyblue  text-xl font-bold">
                        You are currently not Subscribed to any plan! <br></br> To unlock all features click Purchase
                        plan button below
                      </h1>

                      <button
                        type="button"
                        className="flex justify-center gap-2 bg-storyblueSecondLightText text-white font-bold rounded-md text-base items-center mt-5"
                        onClick={() => setPurchasePlan(true)}
                      >
                        Purchase Plan
                      </button>
                    </div>
                  )}
                </div>

                {useSelectMode && (
                  <PrimaryFixedBulkActionManu
                    items={selectedBooks}
                    printLoader={printingLoader}
                    printDisabled={
                      typeof user?.bookFlyersGenerationProgress === 'number' &&
                      user.bookFlyersGenerationProgress >= 0 &&
                      user.bookFlyersGenerationProgress < 100
                    }
                    shipLoader={shippingLoader}
                    onPrint={handlePrintSelectedBooks}
                    onShip={() => setShippingPopup(true)}
                    close={closeSelectMode}
                    purchasedBooksNumber={useSelectMode ? selectedPurchasedBooks : allPurchasedBooks}
                  ></PrimaryFixedBulkActionManu>
                )}
              </div>
            )}
          </div>
          <Dialogs
            openDialog={openDialog}
            handelClose={handleClose}
            handelProcess={() =>
              handleDeleteBook(selectedBook?.bookId!, setDeletingBook, navigateTo, handleClose, handleFetchBooks)
            }
            confirmMessage="Are you sure you want to delete this Story?"
            loader={deletingBook}
          />
        </>
      )}

      {shippingPopup && (
        <Modal
          title={'Confirm Shipment Details'}
          description={'Please review the details below before releasing to print'}
          className={'global-absolute-popup-style secondary-alert'}
          showSubmitButton={false}
          showCloseButton={false}
          showTopRightCloseButton={false}
        >
          <div className="flex">
            <ShippingAddressInfo
              userName={user?.firstName}
              numberBookPurchased={selectedPurchasedBooks}
              shippingAddress={user?.shippingAddresses?.length ? user?.shippingAddresses[0] : undefined}
            ></ShippingAddressInfo>
          </div>
          <div className="flex w-full justify-center items-center pt-3 border-t">
            <button
              onClick={() => {
                setShippingPopup(false);
              }}
              className="bg-transparent py-2 px-6 rounded-full transition-all"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                setShippingPopup(false);
                navigateTo('/updateProfile');
              }}
              className="bg-transparent text-storyblueButton py-2 px-6 rounded-full transition-all"
            >
              Edit address
            </button>
            <button
              onClick={() => {
                setShippingPopup(false);
                handleShipSelectedBooks().then();
              }}
              className="bg-storyblueSecondLightText text-white font-semibold py-2 px-6 rounded-full transition-all"
            >
              Confirm and release to print
            </button>
          </div>
        </Modal>
      )}

      <Footer className="mb-0 pb-4" />
      <PageModal />
    </div>
  );
}

export default BookList;
